import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { A, type RouteSectionProps } from "@solidjs/router";
import withPagePermissionCheck from "../../../utils/withPagePermissionCheck";
import PermissionService from "../../../services/PermissionService";
import { useCustomerState } from "../../../state/customer";
import Text from "../../../components/text/Text";
import UserService from "../../../services/UserService";
import { useTranslation } from "../../../state/i18n";
import { TextAndIconButton } from "../../../components/button/Button";
import { isInvite, resolveFullName, resolveUserOrInviteName } from "../../../utils/userUtil";
import { isEmpty, isEqual, isNil, take } from "lodash-es";
import { getAccountRole, getRoleName, groupRoles, orderRolesByName } from "../../../utils/roleUtil";
import Pill from "../../../components/pill/Pill";
import { condition, narrow } from "../../../utils/solidUtils";
import { IconButtonMenu, ListItemMenu } from "../../../components/listItemMenu/ListItemMenu";
import Pagination from "../../../components/pagination/Pagination";
import { CompactSingleSelect } from "../../../components/menu/Select";
import type { Pagination as PaginationType } from "../../../services/ApiService";
import { useModalManager } from "../../../components/modal/ModalManager";
import { InviteUserModal, InviteUsersModal } from "./InviteUserModal";
import EditUserNameModal from "./EditUserNameModal";
import GenericFailureModal from "../../GenericFailureModal";
import DeleteUserModal from "./DeleteUserModal";
import TransferOwnershipModal from "./TransferOwnershipModal";
import { useUserState } from "../../../state/user";
import type InviteId from "../../../state/InviteId.ts";
import SeparatorLine from "../../../components/SeparatorLine.tsx";
import { IconTile } from "../../../components/icon/IconTile.tsx";
import type { PageSize } from "../../../utils/paginationUtil.ts";
import { pageSizeSelectOptions } from "../../../utils/paginationUtil.ts";
import { MemberAvatar } from "../../../components/avatar/Avatar.tsx";

type InviteType = "single" | "multiple";

const MAX_VISIBLE_PILLS = 2;

export const UsersPage: Component<RouteSectionProps<never>> = () => {
  const { t } = useTranslation();
  const customer = useCustomerState();
  const { open } = useModalManager();

  const [page, setPage] = createSignal(0);
  const [pageSize, setPageSize] = createSignal<PageSize>(20);

  const pagination = createMemo<PaginationType>(() => ({ page: page(), size: pageSize() }));

  const currentUser = useUserState();
  const groupsState = UserService.getGroups(customer.customerId);
  const rolesState = UserService.getRoles(customer.customerId);
  // oxlint-disable-next-line solid/reactivity -- pagination accessor is called inside useQuery's tracked scope
  const usersState = UserService.getPaginatedUsers(customer.customerId, pagination);
  const accountOwner = UserService.getAccountOwner(customer.customerId);
  const resendInvite = UserService.resendInvitation(customer.customerId);

  const [resentInvites, setResentInvites] = createSignal<InviteId[]>([]);
  const addResentInvite = (inviteId: InviteId) => setResentInvites((prev) => [...prev, inviteId]);

  const relevantGroups = createMemo(() => {
    if (isNil(groupsState.data) || isNil(usersState.data)) {
      return {};
    }

    const users = usersState.data.content;
    const allGroupIds = new Set(users.flatMap((user) => user.groups.map((id) => id.toString())));
    return Object.fromEntries(
      groupsState.data
        .filter((group) => allGroupIds.has(group.id.toString()))
        .map((group) => [group.id.toString(), group]),
    );
  });

  const groupedRoles = createMemo(() => {
    if (isNil(rolesState.data)) {
      return;
    }

    return groupRoles(orderRolesByName(rolesState.data, t));
  });

  const userGroupsFoundInCustomer = (user: { groups: { toString(): string }[] }) =>
    user.groups.filter((id) => !isNil(relevantGroups()[id.toString()]));

  const pillsColumn = (labels: string[]) => (
    <>
      <For each={take(labels, MAX_VISIBLE_PILLS)}>{(label) => <Pill label={label} variant="neutral" />}</For>
      <Show when={labels.length > MAX_VISIBLE_PILLS}>
        <Pill label={`+${labels.length - MAX_VISIBLE_PILLS}`} variant="neutral" />
      </Show>
    </>
  );

  const openInviteUserModal = (inviteType: InviteType) => {
    open({
      content: () => (inviteType === "single" ? <InviteUserModal /> : <InviteUsersModal />),
    });
  };

  const userData = createMemo(() =>
    !isNil(usersState.data) && !accountOwner.isPending
      ? {
          users: usersState.data,
          accountOwner: accountOwner.data ?? undefined,
        }
      : undefined,
  );

  return (
    <div class="flex h-full flex-col gap-9">
      <div class="flex justify-between">
        <Text text={t(($) => $.USERS_PAGE_TITLE)} as="h1" class="text-header-xl text-primary" />

        <div class="flex min-w-0 gap-4">
          <Show when={userData()?.accountOwner}>
            {(accountOwner) => (
              <>
                <A href={`/customers/${customer.customerId}/users/${accountOwner().id}`}>
                  <div class="flex items-center gap-2.5 rounded-sm border border-subtle bg-surface-extra-subtle py-1.5 pr-3 pl-2 hover:bg-surface-extra-subtle-hover active:bg-surface-extra-subtle-pressed">
                    <IconTile icon="crown" size="xs" bgColor="bg-accent-subtle-yellow" color="icon-accent-yellow" />
                    <div class="flex gap-1.5 text-base-regular text-secondary">
                      <span class="shrink-0">{t(($) => $.USERS_PAGE_ACCOUNT_OWNER_PREFIX)}</span>
                      <Text class="text-base-medium text-primary" text={resolveFullName(accountOwner())} truncate />
                    </div>
                  </div>
                </A>
                <SeparatorLine orientation="vertical" />
              </>
            )}
          </Show>

          <ListItemMenu<InviteType>
            menu={{
              size: "lg",
              positioning: {
                placement: "bottom-start",
              },
            }}
            trigger={(triggerProps) => (
              <TextAndIconButton
                {...triggerProps}
                icon="plus"
                text={t(($) => $.USERS_PAGE_INVITE_USER)}
                variant="light"
                size="large"
              />
            )}
            items={[
              [
                {
                  type: "action",
                  onClick: () => openInviteUserModal("single"),
                  variant: "icon",
                  label: t(($) => $.USERS_PAGE_INVITE_SINGLE_TITLE),
                  icon: {
                    name: "user",
                    type: "outline",
                  },
                  value: "single",
                },
                {
                  type: "action",
                  onClick: () => openInviteUserModal("multiple"),
                  variant: "icon",
                  label: t(($) => $.USERS_PAGE_INVITE_MULTIPLE_TITLE),
                  icon: {
                    name: "users",
                    type: "outline",
                  },
                  value: "multiple",
                },
              ],
            ]}
          />
        </div>
      </div>
      <Show when={userData()}>
        {(data) => (
          <div
            role="table"
            style={{
              "--column-template": "calc(46% - 2.625rem) 1fr 1fr 2.5rem",
            }}
            class="flex min-h-0 flex-1 flex-col"
          >
            <div role="rowgroup">
              <div
                role="row"
                class="grid grid-cols-template gap-3 border-b border-subtle text-base-semibold text-secondary"
              >
                <div role="columnheader" class="px-3 py-1.5">
                  {t(($) => $.USERS_PAGE_NAME_COLUMN_TITLE)}
                </div>
                <div role="columnheader" class="py-1.5">
                  {t(($) => $.USERS_PAGE_GROUP_COLUMN_TITLE)}
                </div>
                <div role="columnheader" class="py-1.5">
                  {t(($) => $.USERS_PAGE_ROLE_COLUMN_TITLE)}
                </div>
                <div role="columnheader" class="w-10" />
              </div>
            </div>
            <div role="rowgroup" class="min-h-0 flex-1 overflow-y-auto pb-6">
              <For each={data().users.content}>
                {(user) => {
                  const canTransferOwnership = PermissionService.canTransferOwnership(customer.customerId);
                  const isCurrentUser = isEqual(user.id, currentUser().id);
                  const isAccountOwner = isEqual(user.id, data().accountOwner?.id);

                  return (
                    <div class="relative bg-surface-default hover:bg-surface-hover">
                      <A
                        href={user.id.toString()}
                        class="absolute inset-0 rounded-sm -outline-offset-1 focus-visible:outline-default"
                      />
                      <div
                        role="row"
                        class="pointer-events-none relative grid grid-cols-template items-center gap-3 text-base-regular text-secondary"
                      >
                        <div role="cell" class="flex min-h-18 min-w-0 items-center gap-2.5 px-3 py-4">
                          <MemberAvatar member={user} size="md" />
                          <div class="flex min-w-0 flex-col">
                            <div class="flex items-center gap-2">
                              <Text
                                text={resolveUserOrInviteName(isInvite(user), user)}
                                as="span"
                                class="shrink text-base-medium text-primary"
                                truncate
                              />
                              <Show when={narrow(user, isInvite)}>
                                {(invite) => (
                                  <Pill
                                    variant="neutral"
                                    label={t(($) =>
                                      resentInvites().includes(invite().id)
                                        ? $.USERS_PAGE_SSO_INVITED_RESENT
                                        : $.USERS_PAGE_SSO_INVITED,
                                    )}
                                  />
                                )}
                              </Show>
                              <Show when={!isInvite(user) && user.externallyManaged}>
                                <Pill variant="informative" label={t(($) => $.USERS_PAGE_SSO_MANAGED)} />
                              </Show>
                            </div>
                            <Show when={!isInvite(user)}>
                              <Text
                                text={user.emailAddress}
                                as="span"
                                class="text-base-regular text-secondary"
                                truncate
                              />
                            </Show>
                          </div>
                        </div>
                        <div role="cell" class="flex min-w-0 flex-wrap gap-1 py-4">
                          <Show
                            when={condition(userGroupsFoundInCustomer(user), (groupIds) => !isEmpty(groupIds))}
                            fallback={t(($) => $.USERS_PAGE_NO_GROUPS)}
                          >
                            {(existingUserGroupIds) =>
                              pillsColumn(
                                existingUserGroupIds().map((groupId) => relevantGroups()[groupId.toString()].name),
                              )
                            }
                          </Show>
                        </div>
                        <div role="cell" class="flex min-w-0 flex-wrap gap-1 py-4">
                          {(() => {
                            const accountRole = getAccountRole(user.grants, groupedRoles());
                            return isNil(accountRole)
                              ? t(($) => $.USERS_PAGE_NO_ROLES)
                              : pillsColumn([getRoleName(accountRole, t)]);
                          })()}
                        </div>
                        <Show when={isInvite(user) || !user.externallyManaged}>
                          <div role="cell" class="pointer-events-auto">
                            <IconButtonMenu
                              items={[
                                [
                                  ...(isInvite(user)
                                    ? [
                                        {
                                          value: "resend_invite",
                                          type: "action" as const,
                                          onClick: () => {
                                            resendInvite.mutate(user.id, {
                                              onSuccess: () => addResentInvite(user.id),
                                              onError: (err) => {
                                                open({
                                                  content: () => <GenericFailureModal error={err} />,
                                                });
                                              },
                                            });
                                          },
                                          variant: "icon" as const,
                                          label: t(($) => $.USERS_PAGE_RESEND_INVITE),
                                          icon: { name: "refresh" as const, type: "outline" as const },
                                        },
                                      ]
                                    : [
                                        {
                                          value: "edit_name",
                                          type: "action" as const,
                                          onClick: () =>
                                            open({
                                              content: () => <EditUserNameModal user={user} />,
                                            }),
                                          variant: "icon" as const,
                                          label: t(($) => $.USERS_PAGE_EDIT_NAME),
                                          icon: { name: "pencil" as const, type: "outline" as const },
                                        },
                                      ]),
                                  {
                                    value: "transfer_ownership",
                                    type: "action",
                                    onClick: () => open({ content: () => <TransferOwnershipModal /> }),
                                    variant: "icon",
                                    hidden: !canTransferOwnership || isAccountOwner,
                                    label: t(($) => $.USERS_PAGE_TRANSFER_OWNERSHIP),
                                    icon: { name: "switchHorizontal", type: "solid" },
                                  },
                                  {
                                    value: "delete",
                                    type: "action",
                                    onClick: () => open({ content: () => <DeleteUserModal user={user} /> }),
                                    variant: "icon",
                                    hidden: isCurrentUser || isAccountOwner,
                                    label: t(($) =>
                                      isInvite(user) ? $.DELETE_USER_MODAL_REVOKE_TITLE : $.DELETE_BUTTON,
                                    ),
                                    icon: { name: "trash", type: "outline" },
                                  },
                                ],
                              ]}
                              aria-label={t(($) => $.ACTIONS_ARIA)}
                            />
                          </div>
                        </Show>
                      </div>
                    </div>
                  );
                }}
              </For>
              <div class="mt-3 flex justify-between">
                <Pagination
                  initialPage={page() + 1}
                  pageSize={pageSize()}
                  onPageChange={(page) => setPage(page - 1)}
                  totalElements={data().users.totalElements}
                />

                <div class="flex items-center gap-2">
                  <label class="text-small-medium whitespace-nowrap text-secondary">
                    {t(($) => $.USERS_PAGE_SIZE_LABEL)}
                  </label>
                  <CompactSingleSelect
                    ariaLabel={t(($) => $.USERS_PAGE_SIZE_LABEL)}
                    value={pageSize()}
                    options={pageSizeSelectOptions}
                    onSelect={(page) => setPageSize(page)}
                    sameWidth={false}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </Show>
    </div>
  );
};

const ProtectedUsersPage = withPagePermissionCheck(UsersPage, PermissionService.canManagePermissions);

export default ProtectedUsersPage;
